import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  FormChatApiError,
  FormChatFieldInput,
  FormChatMessage,
  FormChatSelectionPayload,
  FormChatSendOptions,
  FormChatStep,
  OnboardingChatSummary,
} from "gsintegrations";
import { trackOnboardingChatBotEvent } from "../analytics/onboardingFormChat";
import type { SignupAnalyticsContext } from "../analytics/signup";
import { useOnboardingFormChatMutation } from "./useOnboardingFormChatMutation";
import {
  onboardingFormChatRepository,
  type OnboardingChatFormState,
  type OnboardingFormChatMessageResponse,
  type OnboardingFormChatReferenceData,
} from "../repositories/onboardingFormChat.repository";
import {
  isListSelectField,
  resolveFieldInput,
  resolveFieldOptions,
  resolveSelectedOption,
} from "../utils/formChatFieldOptions";

import {
  getOnboardingEditableFields,
  resolveOnboardingSummaryFieldValue,
} from "../constants/onboardingChatBot.metadata";

const REVIEW_CONTROL_REPLIES = ["Confirmar", "Editar campos"];

type EditMode = "none" | "select-field" | "await-value";

const createMessage = (
  role: "assistant" | "user" | "system",
  content: string,
): FormChatMessage => ({
  id: `${role}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  role,
  content,
  createdAt: new Date().toISOString(),
});

const TERREIRO_QUICK_STEPS: FormChatStep[] = [
  "personal",
  "terreiro",
  "password",
  "review",
];

const TERREIRO_COMPLETE_STEPS: FormChatStep[] = [
  "personal",
  "terreiro",
  "password",
  "financial",
  "subscription",
  "review",
];

const MEDIUM_QUICK_STEPS: FormChatStep[] = [
  "personal",
  "guardian",
  "password",
  "review",
];

const MEDIUM_COMPLETE_STEPS: FormChatStep[] = [
  "personal",
  "guardian",
  "address",
  "documents",
  "password",
  "review",
];

const TERREIRO_STEP_LABELS: Partial<Record<FormChatStep, string>> = {
  personal: "Dados pessoais",
  terreiro: "Dados do terreiro",
  password: "Senha",
  financial: "Conta bancária",
  subscription: "Mensalidade",
  review: "Revisão",
};

const MEDIUM_STEP_LABELS: Partial<Record<FormChatStep, string>> = {
  personal: "Dados pessoais",
  guardian: "Responsável",
  password: "Senha",
  address: "Endereço",
  documents: "Documentos",
  review: "Revisão",
};

export interface UseOnboardingFormChatParams {
  entityType: "terreiro_onboarding" | "medium_onboarding";
  mode: "quick" | "complete";
  isSocialAuth?: boolean;
  referenceData?: OnboardingFormChatReferenceData;
  prefilledData?: OnboardingChatFormState;
  signupAnalyticsContext: SignupAnalyticsContext;
  onConfirmed: (
    formState: OnboardingChatFormState,
  ) => boolean | void | Promise<boolean | void>;
}

export const useOnboardingFormChat = ({
  entityType,
  mode,
  isSocialAuth = false,
  referenceData,
  prefilledData,
  signupAnalyticsContext,
  onConfirmed,
}: UseOnboardingFormChatParams) => {
  const isFeatureEnabled = (() => {
    if (typeof window !== "undefined") {
      const override = (
        window as Window & { __ONBOARDING_FORM_CHAT_ENABLED__?: boolean }
      ).__ONBOARDING_FORM_CHAT_ENABLED__;
      if (override === true || override === false) {
        return override;
      }
    }
    return import.meta.env.VITE_ONBOARDING_FORM_CHAT_ENABLED !== "false";
  })();
  const isVoiceEnabled = false;

  const { mutateAsync: sendMessageMutation } = useOnboardingFormChatMutation();
  const pendingRequestsRef = useRef(0);
  const [isSending, setIsSending] = useState(false);

  const editableFields = getOnboardingEditableFields(
    entityType,
    isSocialAuth,
    mode,
  );

  const stepOrder =
    entityType === "terreiro_onboarding"
      ? mode === "quick"
        ? TERREIRO_QUICK_STEPS
        : TERREIRO_COMPLETE_STEPS
      : mode === "quick"
        ? MEDIUM_QUICK_STEPS
        : MEDIUM_COMPLETE_STEPS;

  const stepLabels =
    entityType === "terreiro_onboarding"
      ? TERREIRO_STEP_LABELS
      : MEDIUM_STEP_LABELS;

  const [sessionId, setSessionId] = useState<string | undefined>();
  const [messages, setMessages] = useState<FormChatMessage[]>([]);
  const [quickReplies, setQuickReplies] = useState<string[]>([]);
  const [summary, setSummary] = useState<OnboardingChatSummary | undefined>();
  const [currentStep, setCurrentStep] = useState<FormChatStep>("personal");
  const [formState, setFormState] = useState<OnboardingChatFormState>(
    prefilledData || {},
  );
  const [activeField, setActiveField] = useState<string | undefined>();
  const [fieldInput, setFieldInput] = useState<FormChatFieldInput | undefined>();
  const [error, setError] = useState<FormChatApiError | null>(null);
  const [editMode, setEditMode] = useState<EditMode>("none");
  const [editingField, setEditingField] = useState<string | null>(null);
  const hasStartedRef = useRef(false);
  const bootstrappedKeyRef = useRef<string | null>(null);
  const sendChatRequestRef = useRef<
    (
      payload: Omit<
        Parameters<typeof onboardingFormChatRepository.sendMessage>[0],
        "entityType" | "mode" | "isSocialAuth" | "referenceData" | "prefilledData"
      >,
    ) => Promise<OnboardingFormChatMessageResponse | undefined>
  >(() => Promise.resolve(undefined));

  const handleResponseUpdate = useCallback(
    (response: OnboardingFormChatMessageResponse) => {
      setSessionId(response.sessionId);
      setQuickReplies(response.quickReplies || []);
      setSummary(response.summary);
      setFormState(response.formState);
      setActiveField(response.activeField);
      setFieldInput(response.fieldInput);
      setCurrentStep(response.currentStep as FormChatStep);
      setMessages((prev) => {
        const lastAssistant = [...prev]
          .reverse()
          .find((message) => message.role === "assistant");
        if (lastAssistant?.content === response.assistantMessage) {
          return prev;
        }
        return [
          ...prev,
          createMessage("assistant", response.assistantMessage),
        ];
      });
      if (response.currentStep === "review") {
        trackOnboardingChatBotEvent(
          "onboarding_chat_bot_completed",
          signupAnalyticsContext,
          { entityType, mode },
        );
      }
    },
    [entityType, mode, signupAnalyticsContext],
  );

  const sendChatRequest = useCallback(
    async (
      payload: Omit<
        Parameters<typeof onboardingFormChatRepository.sendMessage>[0],
        "entityType" | "mode" | "isSocialAuth" | "referenceData" | "prefilledData"
      >,
    ) => {
      pendingRequestsRef.current += 1;
      setIsSending(true);
      try {
        setError(null);
        const response = await sendMessageMutation({
          entityType,
          mode,
          isSocialAuth,
          referenceData,
          prefilledData,
          ...payload,
        });
        handleResponseUpdate(response);
        return response;
      } catch (err: unknown) {
        const axiosError = err as {
          response?: { data?: { code?: string; error?: string } };
        };
        const code = axiosError?.response?.data?.code || "FORM_CHAT_ERROR";
        const message =
          axiosError?.response?.data?.error ||
          "Não foi possível processar sua mensagem agora.";
        const nextError = { code, message };
        setError(nextError);
        setMessages((prev) => [...prev, createMessage("system", message)]);
        trackOnboardingChatBotEvent(
          "onboarding_chat_bot_error",
          signupAnalyticsContext,
          { entityType, mode, code },
        );
        return undefined;
      } finally {
        pendingRequestsRef.current -= 1;
        setIsSending(pendingRequestsRef.current > 0);
      }
    },
    [
      entityType,
      handleResponseUpdate,
      isSocialAuth,
      mode,
      prefilledData,
      referenceData,
      sendMessageMutation,
      signupAnalyticsContext,
    ],
  );

  sendChatRequestRef.current = sendChatRequest;

  const beginEditingField = useCallback(
    (field: { key: string; label: string }) => {
      setMessages((prev) => [...prev, createMessage("user", field.label)]);
      setEditingField(field.key);
      setActiveField(field.key);
      setFieldInput(resolveFieldInput(field.key, referenceData));

      if (isListSelectField(field.key)) {
        setEditMode("await-value");
        setMessages((prev) => [
          ...prev,
          createMessage(
            "assistant",
            `Selecione o novo valor para ${field.label} na lista.`,
          ),
        ]);
      } else {
        setEditMode("await-value");
        setMessages((prev) => [
          ...prev,
          createMessage(
            "assistant",
            field.key === "password"
              ? "Digite a nova senha."
              : `Qual o novo valor para ${field.label}?`,
          ),
        ]);
      }
      setQuickReplies([]);
    },
    [referenceData],
  );

  const handleSelectOption = useCallback(
    async (selection: FormChatSelectionPayload) => {
      setMessages((prev) => [
        ...prev,
        createMessage("user", selection.optionLabel),
      ]);

      if (!hasStartedRef.current) {
        hasStartedRef.current = true;
        trackOnboardingChatBotEvent(
          "onboarding_chat_bot_started",
          signupAnalyticsContext,
          { entityType, mode },
        );
      }
      trackOnboardingChatBotEvent(
        "onboarding_chat_bot_field_collected",
        signupAnalyticsContext,
        { entityType, mode, field: selection.field },
      );

      const isEditing = editMode === "await-value" && editingField;
      const fieldToEdit = editingField;
      if (isEditing) {
        setEditMode("none");
        setEditingField(null);
      }

      await sendChatRequest({
        sessionId,
        message: "",
        selection,
        ...(isEditing
          ? {
              action: "edit_field" as const,
              editField: fieldToEdit || selection.field,
            }
          : {}),
      });
    },
    [
      editMode,
      editingField,
      entityType,
      mode,
      sendChatRequest,
      sessionId,
      signupAnalyticsContext,
    ],
  );

  const handleSendMessage = useCallback(
    async (message: string, options?: FormChatSendOptions) => {
      const trimmed = message.trim();
      if (!trimmed) return;

      if (editMode === "select-field") {
        const field = editableFields.find(
          (item) => item.label.toLowerCase() === trimmed.toLowerCase(),
        );
        if (field) {
          beginEditingField(field);
          return;
        }
        setEditMode("none");
        setEditingField(null);
      }

      setMessages((prev) => [...prev, createMessage("user", trimmed)]);

      if (editMode === "await-value" && editingField) {
        const fieldToEdit = editingField;
        setEditMode("none");
        setEditingField(null);
        await sendChatRequest({
          sessionId,
          message: trimmed,
          action: "edit_field",
          editField: fieldToEdit,
        });
        return;
      }

      if (!hasStartedRef.current) {
        hasStartedRef.current = true;
        trackOnboardingChatBotEvent(
          "onboarding_chat_bot_started",
          signupAnalyticsContext,
          { entityType, mode },
        );
      }
      trackOnboardingChatBotEvent(
        "onboarding_chat_bot_field_collected",
        signupAnalyticsContext,
        { entityType, mode },
      );

      await sendChatRequest({
        sessionId,
        message: trimmed,
        inputMode: options?.inputMode,
      });
    },
    [
      beginEditingField,
      editMode,
      editableFields,
      editingField,
      entityType,
      mode,
      sendChatRequest,
      sessionId,
      signupAnalyticsContext,
    ],
  );

  const handleEdit = useCallback(() => {
    if (currentStep !== "review") return;
    setEditMode("select-field");
    setEditingField(null);
  }, [currentStep]);

  const handleCancelEdit = useCallback(() => {
    setEditMode("none");
    setEditingField(null);
    setActiveField(undefined);
    setFieldInput(undefined);
    setQuickReplies([]);
    setMessages((prev) => [
      ...prev,
      createMessage(
        "assistant",
        "Edição cancelada. Revise seus dados e confirme quando estiver pronto.",
      ),
    ]);
  }, []);

  const handleEditField = useCallback(
    (fieldKey: string) => {
      if (currentStep !== "review") return;
      const field = editableFields.find((item) => item.key === fieldKey);
      if (!field) return;
      beginEditingField(field);
    },
    [beginEditingField, currentStep, editableFields],
  );

  const clearConversation = useCallback(() => {
    setSessionId(undefined);
    setMessages([]);
    setQuickReplies([]);
    setSummary(undefined);
    setFormState(prefilledData || {});
    setActiveField(undefined);
    setFieldInput(undefined);
    setCurrentStep("personal");
    setError(null);
    setEditMode("none");
    setEditingField(null);
    hasStartedRef.current = false;
  }, [prefilledData]);

  const bootstrapKey = useMemo(
    () =>
      JSON.stringify({
        entityType,
        mode,
        isSocialAuth,
        prefilledData,
      }),
    [entityType, isSocialAuth, mode, prefilledData],
  );

  const bootstrapConversation = useCallback(async () => {
    await sendChatRequestRef.current({ message: "" });
  }, []);

  const resetConversation = useCallback(() => {
    clearConversation();
    void bootstrapConversation();
  }, [bootstrapConversation, clearConversation]);

  const handleConfirm = useCallback(async () => {
    if (!sessionId) return;
    const response = await sendChatRequest({
      sessionId,
      message: "",
      action: "confirm",
    });
    if (!response?.isComplete) return;

    trackOnboardingChatBotEvent(
      "onboarding_chat_bot_submitted",
      signupAnalyticsContext,
      { entityType, mode },
    );
    const confirmed = await onConfirmed(response.formState);
    if (confirmed === false) {
      setCurrentStep("review");
    }
  }, [
    entityType,
    mode,
    onConfirmed,
    sendChatRequest,
    sessionId,
    signupAnalyticsContext,
  ]);

  const handleTranscribeAudio = useCallback(async () => {
    throw new Error("Entrada por voz não está disponível no cadastro.");
  }, []);

  const quickReplyOptions = useMemo(() => {
    if (editMode === "select-field") {
      return editableFields.map((field) => field.label);
    }
    return quickReplies.filter(
      (reply) => !REVIEW_CONTROL_REPLIES.includes(reply),
    );
  }, [editMode, editableFields, quickReplies]);

  useEffect(() => {
    if (bootstrappedKeyRef.current === bootstrapKey) {
      return;
    }

    bootstrappedKeyRef.current = bootstrapKey;
    clearConversation();
    void sendChatRequestRef.current({ message: "" });
  }, [bootstrapKey, clearConversation]);

  const effectiveActiveField =
    editMode === "await-value" && editingField ? editingField : activeField;

  const fieldOptions = useMemo(
    () => resolveFieldOptions(effectiveActiveField, referenceData),
    [effectiveActiveField, referenceData],
  );

  const effectiveFieldInput = useMemo(
    () =>
      resolveFieldInput(effectiveActiveField, referenceData, fieldInput),
    [effectiveActiveField, fieldInput, referenceData],
  );

  const selectedOption = useMemo(
    () =>
      resolveSelectedOption(
        effectiveActiveField,
        formState as Record<string, unknown>,
        fieldOptions,
      ),
    [effectiveActiveField, fieldOptions, formState],
  );

  const editInteraction =
    editMode === "none" ? null : (editMode as "select-field" | "await-value");

  const editingFieldLabel = useMemo(() => {
    if (!editingField) return undefined;
    return editableFields.find((field) => field.key === editingField)?.label;
  }, [editableFields, editingField]);

  const editableFieldOptions = useMemo(
    () =>
      editableFields.map((field) => ({
        key: field.key,
        label: field.label,
        currentValue: resolveOnboardingSummaryFieldValue(field.key, summary),
      })),
    [editableFields, summary],
  );

  return {
    isFeatureEnabled,
    isVoiceEnabled,
    messages,
    quickReplies: quickReplyOptions,
    summary,
    currentStep,
    stepOrder,
    stepLabels,
    activeField: effectiveActiveField,
    fieldInput: effectiveFieldInput,
    fieldOptions,
    selectedOption,
    isLoading: isSending,
    error,
    entityType,
    handleSendMessage,
    handleSelectOption,
    handleConfirm,
    handleEdit,
    handleCancelEdit,
    handleEditField,
    handleTranscribeAudio,
    resetConversation,
    editInteraction,
    editingFieldLabel,
    editableFieldOptions,
  };
};
